import axios from 'axios';
import { store } from 'store';
import { fetchUpdateCompanyPlatforms } from 'store/slices/companyPlatforms';
import { ChatUserModel } from 'types/chat-users/chat-user';
import type { PriorityCompany, PriorityConnection } from 'types/company-platforms/priority';
import {
  PriorityDocInfo,
  PriorityDocType,
  PriorityFormat,
  PriorityListDocument,
  PriorityPlatformsSettings,
} from 'types/company-platforms/priority';
import { fetcher } from 'utils/axios';
import { safeParseJSON } from 'utils/helpers';
import { invalidatePrioritySendDocsReadinessCache } from 'utils/prioritySendDocsReadiness';
import type { Procedure } from 'views/priority/priority-settings-page/companies/ProceduresList';

// Priority Comapies
export const fetchCreatePriorityCompany = async (
  company: Omit<PriorityCompany, 'id' | 'createdOn' | 'createdBy' | 'updatedOn' | 'updatedBy' | 'companyId'>,
) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/priority/companies',
        {
          data: company,
        },
      ],
      'newapi',
    );

    if (data?.result) {
      // Update the company platforms in the store
      const storeState = store.getState();
      const updatedCompanyPlatforms = { ...storeState.companyPlatforms.priority, companies: data.data };
      await fetchUpdateCompanyPlatforms({ priority: updatedCompanyPlatforms });
      invalidatePrioritySendDocsReadinessCache();
    }

    return data;
  } catch (error) {
    console.warn('Error in fetchCreatePriorityCompany:', error);
    return null;
  }
};

export const fetchGetPriorityCompanies = async () => {
  try {
    const data = await fetcher('get', '/api/priority/companies', 'newapi');
    if (data.error) {
      throw new Error(data.error);
    }
    if (data?.data) return data.data as Array<PriorityCompany>;
  } catch (error) {
    console.warn('Error in fetchGetPriorityCompanies:', error);
    return [] as Array<PriorityCompany>;
  }
};

export const fetchUpdatePriorityCompany = async (company: PriorityCompany) => {
  try {
    const data = await fetcher(
      'put',
      [
        `/api/priority/companies/${company.id}`,
        {
          data: company,
        },
      ],
      'newapi',
    );

    if (data?.result) {
      // Update the company platforms in the store
      const storeState = store.getState();
      const updatedCompanyPlatforms = { ...storeState.companyPlatforms.priority, companies: data.data };
      await fetchUpdateCompanyPlatforms({ priority: updatedCompanyPlatforms });
      invalidatePrioritySendDocsReadinessCache();
    }

    return data;
  } catch (error) {
    console.warn('Error in fetchUpdatePriorityCompany:', error);
    return null;
  }
};

export const fetchDeletePriorityCompany = async (companyId: string) => {
  try {
    const data = await fetcher('delete', `/api/priority/companies/${companyId}`, 'newapi');

    if (data?.result) {
      // Update the company platforms in the store
      const storeState = store.getState();
      const updatedCompanyPlatforms = { ...storeState.companyPlatforms.priority, companies: data.data };
      await fetchUpdateCompanyPlatforms({ priority: updatedCompanyPlatforms });
      invalidatePrioritySendDocsReadinessCache();
    }

    return data;
  } catch (error) {
    console.warn('Error in fetchDeletePriorityCompany:', error);
    return null;
  }
};

//* procedures
export const fetchGetPriorityDocTypes = async () => {
  try {
    const data = await fetcher('get', '/api/priority/getDocTypes', 'api');

    if (data) return data as Array<PriorityDocType>;
    else return null;
  } catch (error) {
    console.warn('Error in fetchGetPriorityDocTypes:', error);
    return null;
  }
};
export const fetchCreateEditProcedure = async (
  procedure: Omit<PriorityDocType, 'formats' | 'companyId' | 'id'>,
) => {
  try {
    const data = await fetcher(
      'put',
      [
        `/priorityProceedures/${procedure.procedureName}`,
        {
          data: procedure,
        },
      ],
      'newapi',
    );

    // if (data) return data as PriorityDocType;
    if (data) return data;
    else return null;
  } catch (error) {
    console.warn('Error in fetchCreateEditProcedure:', error);
    return null;
  }
};
export const fetchDeleteProcedure = async (procedureName: string) => {
  try {
    const data = await fetcher('delete', `/priorityProceedures/${procedureName}`, 'api');

    // if (data) return data as PriorityDocType;
    if (data) return data;
    else return null;
  } catch (error) {
    console.warn('Error in fetchDeleteProcedure:', error);
    return null;
  }
};
export const fetchDuplicateProcedure = async (procedureName: string) => {
  try {
    const data = await fetcher('put', `/priorityProceedures/duplicate/${procedureName}`, 'api');

    if (data) return data;
    else return null;
  } catch (error) {
    console.warn('Error in fetchDuplicateProcedure:', error);
    return null;
  }
};

export type SaveCompanyProcedureType = {
  CompanyId: string;
  DateUpdated: string;
  FormatObject: string | null;
  Id: number;
  ProcedureName: string;
};
export const saveAllCompanyFormats = async () => {
  try {
    const data = await fetcher('get', '/api/priority/saveAllCompanyFormats', 'priority');

    if (data)
      return data as {
        result: boolean;
        companyFormats: Array<SaveCompanyProcedureType>;
      };
    else return null;
  } catch (error) {
    console.warn('Error in saveAllCompanyFormats:', error);
    return null;
  }
};

//* docs
export type FetchGetPriorityDocsListParams = {
  priorityCompanyId?: string;
  docType: string;
  docSearchIndex?: string;
  from?: string;
  to?: string;
  name?: string;
  docId?: string;
  filter?: string;
  top?: number;
  skip: number;
};
export const fetchGetPriorityDocsList = async (params: FetchGetPriorityDocsListParams) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/priority/getPriorityDocsList',
        {
          data: params,
        },
      ],
      'newapi',
    );

    if (data) {
      return data as {
        error: string;
        result: boolean;
        docs: string;
        url: string;
        procedureObject: {
          formats: Array<PriorityFormat>;
          sorting: string;
          title: string;
          docTitle: string;
          procedureName: string;
          docIndetificator: string;
          idIdentifier: string;
          sdkLinkType: string;
        };
        formats?: {
          companyId: string;
          dateUpdated: string;
          formatObject: string | null;
          id: number;
        } | null;
      };
    } else {
      // אם השרת החזיר תגובה ריקה
      return {
        error: 'השרת החזיר תגובה ריקה',
        result: false,
        docs: '',
        url: '',
        procedureObject: null,
      } as any;
    }
  } catch (error: any) {
    console.warn('Error in fetchGetPriorityDocsList:', error);

    // החזרת אובייקט שגיאה במקום null
    return {
      error: error?.message || error?.response?.data?.message || 'שגיאה בתקשורת עם השרת',
      result: false,
      docs: '',
      url: '',
      procedureObject: null,
      isNetworkError: true,
    } as any;
  }
};
export const fetchGetPriorityDocFormatsFromDoc = async (
  docId: string,
  docType: string,
  procedureName: string,
  signedDoc?: boolean,
) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/priority/getPriorityDoc',
        {
          data: { docId, docType, procedureName, signedDoc },
        },
      ],
      'priority',
    );

    if (data)
      return data as {
        formats: Array<PriorityFormat> | null;
        errorMessage: string | null;
      };
    else return null;
  } catch (error) {
    console.warn('Error in fetchGetPriorityDocFormatsFromDoc:', error);
    return null;
  }
};

export const fetchGetPriorityDocumentInfo = async (
  priorityCompanyId: string,
  docId: string,
  docType: string,
  procedureId: string,
  format: string | number,
  isAutomail?: boolean,
  isWord?: boolean,
) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/priority/getPriorityDoc',
        {
          data: {
            priorityCompanyId,
            docId,
            docType,
            procedureId,
            format,
            isAutomail,
            isWord,
          },
        },
      ],
      'newapi',
    );

    if (data) return data as PriorityDocInfo;
    else return null;
  } catch (error) {
    console.warn('Error in fetchGetPriorityDocumentInfo:', error);
    return null;
  }
};

export interface PriorityDocAttachment {
  description: string | null;
  fileNumber: number;
  fileName: string | null;
  suffix: string | null;
  dateCreated: string | null;
  fileSize: number | null;
  userLogin: string | null;
  lastUpdate: string | null;
}

//* New Priority Node.js endpoints (server-nodejs)

/**
 * Fetch documents list with attachments in a single call via server-nodejs.
 * Replaces separate calls to getPriorityDocsList + getPriorityDocAttachments.
 */
export type FetchGetDocsWithAttachmentsParams = {
  priorityCompanyId: string;
  docType: string;
  docSearchIndex: string;
  docId?: string;
  name?: string;
  top?: number;
  skip?: number;
};

export interface DocsWithAttachmentsResponse {
  result: boolean;
  url?: string;
  docs?: {
    '@odata.context'?: string;
    value: Array<
      PriorityListDocument & {
        EXTFILES_SUBFORM?: Array<{
          EXTFILEDES?: string;
          EXTFILENUM?: number;
          EXTFILENAME?: string;
          SUFFIX?: string;
          CURDATE?: string;
          FILESIZE?: number;
          USERLOGIN?: string;
          UDATE?: string;
        }>;
      }
    >;
  };
  error?: string;
}

export const fetchGetDocsWithAttachments = async (
  params: FetchGetDocsWithAttachmentsParams,
): Promise<DocsWithAttachmentsResponse | null> => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/priority/getDocsWithAttachments',
        {
          data: params,
        },
      ],
      'servernode',
    );

    if (data) return data as DocsWithAttachmentsResponse;
    return { result: false, error: 'לא התקבלה תשובה מהשרת' };
  } catch (error) {
    console.warn('Error in fetchGetDocsWithAttachments:', error);
    return { result: false, error: 'שגיאה בטעינת מסמכים ונספחים' };
  }
};

/**
 * Convert a base64 data URI attachment to a file URL via server-nodejs.
 * Used when EXTFILENAME is already a base64 data URI.
 */
export interface ConvertAttachmentResponse {
  success: boolean;
  fileUrl?: string;
  fileName?: string;
  mimeType?: string;
  fileSize?: number;
  error?: string;
}

export const fetchConvertAttachmentToFile = async (
  base64DataUri: string,
  originalFileName?: string,
): Promise<ConvertAttachmentResponse> => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/priority/convertAttachmentToFile',
        {
          data: {
            base64DataUri,
            originalFileName,
          },
        },
      ],
      'servernode',
    );

    if (data) return data as ConvertAttachmentResponse;
    return { success: false, error: 'לא התקבלה תשובה מהשרת' };
  } catch (error) {
    console.warn('Error in fetchConvertAttachmentToFile:', error);
    return { success: false, error: 'שגיאה בהמרת הנספח' };
  }
};

/**
 * Sync default procedures and formats for all Priority companies of the current owner.
 * Calls the Node.js server endpoint.
 */
export interface SyncDefaultProceduresResponse {
  result: boolean;
  companiesUpdated?: number;
  proceduresAdded?: number;
  formatsAdded?: number;
  message?: string;
  error?: string;
}

/** חילוץ הודעת שגיאה קריאה מתשובת servernode (AxiosError) */
function extractServerNodeErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as Record<string, unknown> | undefined;
    if (body && typeof body === 'object') {
      for (const key of ['message', 'error', 'Message', 'Error', 'detail']) {
        const v = body[key];
        if (v != null && String(v).trim() !== '') return String(v);
      }
    }
    if (error.response?.status) return `${fallback} (HTTP ${error.response.status})`;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * יצירת רשומה בפריוריטי לפי JSON — endpoint של web3 (server-nodejs).
 * הגוף: objectId + priorityCompanyId + שדות הישות עצמם (שטוחים).
 * משמש להרצת בדיקה של מודול "צור אובייקט Priority" בבונה הבוטים.
 */
export const fetchCreatePriorityObjectByJson = async (params: {
  objectId: string;
  priorityCompanyId: string;
  fields: Record<string, string>;
}): Promise<unknown> => {
  try {
    return await fetcher(
      'post',
      [
        '/api/priority/createPriorityObjectByJson',
        {
          data: {
            objectId: params.objectId,
            priorityCompanyId: params.priorityCompanyId,
            ...params.fields,
          },
        },
      ],
      'servernode',
    );
  } catch (error) {
    throw new Error(extractServerNodeErrorMessage(error, 'שגיאה ביצירת רשומה בפריוריטי'));
  }
};

/**
 * שליפת רשומה מפריוריטי לפי שדה חיפוש — endpoint של web3 (server-nodejs).
 * משמש להרצת בדיקה של מודול "קבל אובייקט Priority" בבונה הבוטים.
 */
export const fetchGetPriorityObjectByJson = async (params: {
  objectId: string;
  priorityCompanyId: string;
  searchParamKey: string;
  searchParamValue: string;
}): Promise<unknown> => {
  try {
    return await fetcher(
      'post',
      ['/api/priority/getPriorityObjectByJson', { data: params }],
      'servernode',
    );
  } catch (error) {
    throw new Error(extractServerNodeErrorMessage(error, 'שגיאה בשליפת רשומה מפריוריטי'));
  }
};

export const fetchSyncDefaultProcedures = async (): Promise<SyncDefaultProceduresResponse> => {
  try {
    const data = await fetcher('post', '/api/priority/sync-default-procedures', 'servernode');

    if (data) return data as SyncDefaultProceduresResponse;
    return { result: false, error: 'לא התקבלה תשובה מהשרת' };
  } catch (error) {
    console.warn('Error in fetchSyncDefaultProcedures:', error);
    return { result: false, error: 'שגיאה בסנכרון פרוצדורות' };
  }
};

//* settings
export const submitPriorityParams = async (
  params: Partial<PriorityPlatformsSettings>,
  sendNotification: boolean = true,
) => {
  const storeState = store.getState();

  const newParams = { ...storeState.companyPlatforms.priority, ...params };
  return await fetchUpdateCompanyPlatforms({ priority: newParams }, sendNotification);
};

//* contacts & customers
export const fetchGetPriorityCustomers = async (params: {
  name: string;
  top: number;
  skip: number;
  priorityCompanyId?: string;
  searchTerm?: string;
}) => {
  try {
    
    // אם יש searchTerm, נשתמש ב-API החדש עם OData
    if (params.searchTerm && params.priorityCompanyId) {
      
      // בניית פילטר OData לחיפוש
      const searchTerm = params.searchTerm;
      const filter = `PHONE EQ '${searchTerm}*' OR CUSTNAME EQ '${searchTerm}*' OR CUSTDES EQ '${searchTerm}*'`;
      
      const queryParams = new URLSearchParams({
        '$TOP': params.top.toString(),
        '$SKIP': params.skip.toString(),
        '$FILTER': filter
      });
      
      const url = `/api/priority/query/${params.priorityCompanyId}/CUSTOMERS?${queryParams.toString()}`;
    
      const data = await fetcher('get', url, 'newapi');
    
      if (data && data.data) {
        // המרת התגובה לפורמט הצפוי - הנתונים נמצאים ב-data.data
    
        return {
          contacts: JSON.stringify({ value: [] }), // ריק כי אנחנו מחפשים לקוחות
          customers: JSON.stringify(data.data) // מעביר את data.data שמכיל את value
        };
      }
      return null;
    }
    
    // שימוש ב-API הקיים אם אין חיפוש
    const data = await fetcher(
      'get',
      [
        '/api/priority/getPriorityContacts',
        {
          data: params,
        },
      ],
      'newapi',
    );

    if (data) return data as { contacts: string; customers: string };
    else return null;
  } catch (error) {
    console.warn('Error in fetchGetPriorityCustomers:', error);
    return null;
  }
};

//* contacts (PHONEBOOK)
export const fetchGetPriorityContacts = async (params: {
  name: string;
  top: number;
  skip: number;
  priorityCompanyId?: string;
  searchTerm?: string;
}) => {
  try {
    
    // אם יש searchTerm, נשתמש ב-API החדש עם OData
    if (params.searchTerm && params.priorityCompanyId) {
      
      // בניית פילטר OData לחיפוש ב-PHONEBOOK
      const searchTerm = params.searchTerm;
      const filter = `PHONENUM EQ '${searchTerm}*' OR NAME EQ '${searchTerm}*'`;
      
      const queryParams = new URLSearchParams({
        '$TOP': params.top.toString(),
        '$SKIP': params.skip.toString(),
        '$FILTER': filter
      });
      
      const url = `/api/priority/query/${params.priorityCompanyId}/PHONEBOOK?${queryParams.toString()}`;
      
      const data = await fetcher('get', url, 'newapi');
      
      if (data && data.data) {
        // המרת התגובה לפורמט הצפוי - הנתונים נמצאים ב-data.data
      
        return {
          contacts: JSON.stringify(data.data), // מעביר את data.data שמכיל את value
          customers: JSON.stringify({ value: [] }) // ריק כי אנחנו מחפשים אנשי קשר
        };
      }
      return null;
    }
    
    // שימוש ב-API הקיים אם אין חיפוש
    const data = await fetcher(
      'get',
      [
        '/api/priority/getPriorityContacts',
        {
          data: params,
        },
      ],
      'newapi',
    );

    if (data) return data as { contacts: string; customers: string };
    else return null;
  } catch (error) {
    console.warn('Error in fetchGetPriorityContacts:', error);
    return null;
  }
};
export const fetchFindPriorityContactByName = async (params: {
  name: string;
  top: number;
  skip: number;
  priorityCompanyId?: string;
  searchTerm?: string;
}) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/priority/findPriorityContactByName',
        {
          data: params,
        },
      ],
      'priority',
    );

    if (data) return data as { contacts: string; customers: string };
    else return null;
  } catch (error) {
    console.warn('Error in fetchFindPriorityContactByName:', error);
    return null;
  }
};

export const fetchFindPriorityCustomerByName = async (params: {
  name: string;
  top: number;
  skip: number;
  priorityCompanyId?: string;
  searchTerm?: string;
}) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/priority/findPriorityCustomerByName',
        {
          data: params,
        },
      ],
      'priority',
    );

    if (data) return data as { contacts: string; customers: string };
    else return null;
  } catch (error) {
    console.warn('Error in fetchFindPriorityCustomerByName:', error);
    return null;
  }
};

/** ישות למשימות תפוצה — לקוחות / אנשי קשר (פנקס) / ספקים (OData SUPPLIERS) */
export type PriorityBroadcastEntityKind = 'customers' | 'contacts' | 'suppliers';

/** חילוץ מערך הרשומות (value) מתשובת ‎/api/priority/query/…‎ בכל הצורות שהשרת מחזיר */
function extractODataValue(data: unknown): Record<string, unknown>[] {
  if (!data) return [];
  const payload = (data as { data?: unknown }).data ?? data;
  if (typeof payload === 'string') {
    const o = safeParseJSON(payload);
    return Array.isArray(o?.value) ? (o.value as Record<string, unknown>[]) : [];
  }
  if (payload && typeof payload === 'object' && 'value' in payload) {
    const v = (payload as { value: unknown }).value;
    return Array.isArray(v) ? (v as Record<string, unknown>[]) : [];
  }
  return [];
}

/**
 * שליפת רשומות מכל מסך בפריוריטי דרך ה־endpoint הגנרי ‎/api/priority/query/{companyId}/{SCREEN}‎,
 * עם ‎$FILTER‎ אופציונלי (תחביר הפילטר נבנה ב־utils/priorityQuerySyntax).
 */
export const fetchPriorityScreenRecords = async (params: {
  priorityCompanyId: string;
  screen: string;
  filter?: string;
  top?: number;
  skip?: number;
}): Promise<Record<string, unknown>[] | null> => {
  const queryParams = new URLSearchParams({
    $TOP: String(params.top ?? 1000),
    $SKIP: String(params.skip ?? 0),
  });
  if (params.filter && params.filter.trim() !== '') {
    queryParams.set('$FILTER', params.filter);
  }
  const url = `/api/priority/query/${params.priorityCompanyId}/${params.screen}?${queryParams.toString()}`;

  try {
    const data = await fetcher('get', url, 'newapi');
    if (!data) return null;
    if ((data as { error?: string }).error) {
      throw new Error((data as { error: string }).error);
    }
    return extractODataValue(data);
  } catch (error) {
    console.warn('Error in fetchPriorityScreenRecords:', error);
    throw error instanceof Error ? error : new Error('שגיאה בשליפת רשומות מפריוריטי');
  }
};

/**
 * טעינת רשומות לתפוצה מאותם מקורות כמו מסכי סנכרון הפריוריטי (לקוחות / אנשי קשר),
 * וספקים דרך ‎/api/priority/query/…/SUPPLIERS‎.
 */
export const fetchPriorityBroadcastRecords = async (params: {
  priorityCompanyId: string;
  entity: PriorityBroadcastEntityKind;
  top?: number;
  skip?: number;
}): Promise<Record<string, unknown>[] | null> => {
  const top = params.top ?? 1000;
  const skip = params.skip ?? 0;
  const { priorityCompanyId: id, entity } = params;

  try {
    if (entity === 'customers') {
      const data = await fetchGetPriorityCustomers({
        name: '',
        top,
        skip,
        priorityCompanyId: id,
      });
      if (!data?.customers) return null;
      const o = safeParseJSON(data.customers);
      return Array.isArray(o?.value) ? (o.value as Record<string, unknown>[]) : [];
    }
    if (entity === 'contacts') {
      const data = await fetchGetPriorityContacts({
        name: '',
        top,
        skip,
        priorityCompanyId: id,
      });
      if (!data?.contacts) return null;
      const o = safeParseJSON(data.contacts);
      return Array.isArray(o?.value) ? (o.value as Record<string, unknown>[]) : [];
    }
    const queryParams = new URLSearchParams({
      $TOP: String(top),
      $SKIP: String(skip),
    });
    const url = `/api/priority/query/${id}/SUPPLIERS?${queryParams.toString()}`;
    const data = await fetcher('get', url, 'newapi');
    if (!data) return null;
    const payload = (data as { data?: unknown }).data ?? data;
    if (typeof payload === 'string') {
      const o = safeParseJSON(payload);
      return Array.isArray(o?.value) ? (o.value as Record<string, unknown>[]) : [];
    }
    if (payload && typeof payload === 'object' && payload !== null && 'value' in payload) {
      const v = (payload as { value: unknown }).value;
      return Array.isArray(v) ? (v as Record<string, unknown>[]) : [];
    }
    return [];
  } catch (error) {
    console.warn('fetchPriorityBroadcastRecords:', error);
    return null;
  }
};

export const fetchGetPriorityChatUserObject = async (phoneNumberId: string, userName?: string) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/services/getPriorityChatUserObject',
        {
          data: { phoneNumberId, userName },
        },
      ],
      'priority',
    );

    if (data) return data as ChatUserModel;
    else return null;
  } catch (error) {
    console.warn('Error in fetchGetPriorityChatUserObject:', error);
    return null;
  }
};
export const fetchGetPriorityCustomerPhonebook = async (params: { name: string }) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/priority/getPriorityCustomerPhonebook',
        {
          data: params,
        },
      ],
      'priority',
    );

    if (data) return data as { phonebook: string; result: boolean };
    else return null;
  } catch (error) {
    console.warn('Error in fetchGetPriorityCustomerPhonebook:', error);
    return null;
  }
};

// Priority Connections
// עדכון חתימות הפונקציות ל-camelCase
export const fetchCreatePriorityConnection = async (
  connection: Omit<
    PriorityConnection,
    'connectionId' | 'createdOn' | 'createdBy' | 'updatedOn' | 'updatedBy' | 'companyId'
  >,
) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/priority/connections',
        {
          data: connection,
        },
      ],
      'newapi',
    );
    return data;
  } catch (error) {
    console.warn('Error in fetchCreatePriorityConnection:', error);
    return null;
  }
};

export const fetchGetPriorityConnections = async () => {
  try {
    const data = await fetcher('get', '/api/priority/connections', 'newapi');
    if (data.error) {
      throw new Error(data.error);
    }
    if (data?.data) return data.data as Array<PriorityConnection>;
  } catch (error) {
    console.warn('Error in fetchGetPriorityConnections:', error);
    return [] as Array<PriorityConnection>;
  }
};

export const fetchUpdatePriorityConnection = async (connection: PriorityConnection) => {
  try {
    const data = await fetcher(
      'put',
      [
        `/api/priority/connections/${connection.connectionId}`,
        {
          data: connection,
        },
      ],
      'newapi',
    );
    return data;
  } catch (error) {
    console.warn('Error in fetchUpdatePriorityConnection:', error);
    return null;
  }
};

export const fetchDeletePriorityConnection = async (connectionId: string) => {
  try {
    const data = await fetcher('delete', `/api/priority/connections/${connectionId}`, 'newapi');
    return data;
  } catch (error) {
    console.warn('Error in fetchDeletePriorityConnection:', error);
    return null;
  }
};

export const fetchGetCompanyProcedureFormats = async (prCompanyId: string, procId: string) => {
  try {
    const data = await fetcher(
      'get',
      [`/api/priority/companies/${prCompanyId}/procedures/${procId}/formats`, {}],
      'newapi',
    );
    if (data?.data && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  } catch (error) {
    console.warn('Error in fetchGetCompanyProcedureFormats:', error);
    return [];
  }
};

/** פורמט בודד של פרוצדורה (PrFormats) — הצורה שהשרת מחזיר ב-GET/PUT */
export interface ProcedureFormat {
  formatId: string;
  prCompanyId: string;
  procedureId: string;
  formatName: string | null;
  formatNumber: string | null;
  formatStatus: boolean;
  formatType: string;
}

// Update a single procedure format (rename / enable-disable)
export const fetchUpdateProcedureFormat = async (
  prCompanyId: string,
  procId: string,
  formatId: string,
  changes: Partial<Pick<ProcedureFormat, 'formatName' | 'formatNumber' | 'formatStatus' | 'formatType'>>,
): Promise<{ data: ProcedureFormat | null; error: string | null }> => {
  try {
    const data = await fetcher(
      'put',
      [`/api/priority/companies/${prCompanyId}/procedures/${procId}/formats/${formatId}`, { data: changes }],
      'newapi',
    );
    return { data: data?.data ?? null, error: data?.error ?? null };
  } catch (error) {
    console.warn('Error in fetchUpdateProcedureFormat:', error);
    return { data: null, error: 'שגיאה בעדכון הפורמט' };
  }
};

// Get procedures for a specific Priority company
export const fetchGetCompanyProcedures = async (prCompanyId: string) => {
  try {
    const data = await fetcher('get', `/api/priority/companies/${prCompanyId}/procedures`, 'newapi');
    if (data?.data) return data.data;
    return [];
  } catch (error) {
    console.warn('Error in fetchGetCompanyProcedures:', error);
    return [];
  }
};

// Create a new procedure for a company
export const fetchCreateCompanyProcedure = async (
  prCompanyId: string,
  procedure: Omit<
    Procedure,
    'procId' | 'createdOn' | 'createdBy' | 'updatedOn' | 'updatedBy' | 'companyId' | 'prCompanyId'
  >,
) => {
  try {
    const data = await fetcher(
      'post',
      [`/api/priority/companies/${prCompanyId}/procedures`, { data: procedure }],
      'newapi',
    );
    return data;
  } catch (error) {
    console.warn('Error in fetchCreateCompanyProcedure:', error);
    return null;
  }
};

// Update a procedure for a company
export const fetchUpdateCompanyProcedure = async (prCompanyId: string, procedure: Procedure) => {
  try {
    const data = await fetcher(
      'put',
      [`/api/priority/companies/${prCompanyId}/procedures/${procedure.procId}`, { data: procedure }],
      'newapi',
    );
    return data;
  } catch (error) {
    console.warn('Error in fetchUpdateCompanyProcedure:', error);
    return null;
  }
};

// Delete a procedure for a company
export const fetchDeleteCompanyProcedure = async (prCompanyId: string, procId: string) => {
  try {
    const data = await fetcher(
      'delete',
      `/api/priority/companies/${prCompanyId}/procedures/${procId}`,
      'newapi',
    );
    return data;
  } catch (error) {
    console.warn('Error in fetchDeleteCompanyProcedure:', error);
    return null;
  }
};

// Get all procedures from all companies with company information
export const fetchGetAllCompanyProcedures = async () => {
  try {
    const data = await fetcher('get', '/api/priority/companies/procedures', 'newapi');
    if (data?.data) return data.data;
    return [];
  } catch (error) {
    console.warn('Error in fetchGetAllCompanyProcedures:', error);
    return [];
  }
};

// Sync procedure formats for a company procedure
export const fetchSyncProcedureFormats = async (prCompanyId: string, procedureId: string) => {
  try {
    const data = await fetcher(
      'post',
      `/api/priority/companies/${prCompanyId}/procedures/${procedureId}/sync-formats`,
      'newapi',
    );
    return data;
  } catch (error) {
    console.warn('Error in fetchSyncProcedureFormats:', error);
    return null;
  }
};

// New Priority Config API calls
export interface PriorityConfigData {
  priorityFullApiUrl: string | null;
  priorityApiUrl: string | null;
  priorityUiUrl: string | null;
  priorityTabula: string | null;
  priorityLinkType: string | null;
  priorityToken: string | null;
  priorityConnectionVersion: string | null;
  /** חיבור דרך הקונקטור של פאנר (ברירת מחדל) או API ישיר עם רישיון הלקוח */
  priorityIsConnector?: boolean | null;
}

export const fetchGetPriorityConfig = async (): Promise<PriorityConfigData | null> => {
  try {
    const data = await fetcher('get', '/api/priority/getPriorityConfig', 'newapi');
    if (data?.data) {
      return data.data as PriorityConfigData;
    }
    return null;
  } catch (error) {
    console.warn('Error in fetchGetPriorityConfig:', error);
    return null;
  }
};

export const fetchUpdatePriorityConfig = async (
  config: Partial<PriorityConfigData>,
): Promise<{ success: boolean; message: string }> => {
  try {
    const data = await fetcher(
      'put',
      [
        '/api/priority/updatePriorityConfig',
        {
          data: config,
        },
      ],
      'newapi',
    );

    if (data?.error) {
      return { success: false, message: data.error };
    } else {
      invalidatePrioritySendDocsReadinessCache();
      return { success: true, message: data?.data || 'הגדרות פריוריטי עודכנו בהצלחה' };
    }
  } catch (error) {
    console.warn('Error in fetchUpdatePriorityConfig:', error);
    return { success: false, message: 'שגיאה בתקשורת עם השרת' };
  }
};

// ─── סנכרון אנשי קשר לפריוריטי (מודול priority-sync בנוד) ────────────────────

export type PrioritySyncResult = {
  status:
    | 'synced'
    | 'synced_name_manual'
    | 'created'
    | 'not_found'
    | 'ambiguous'
    | 'create_blocked_no_customer'
    | 'error'
    | 'disabled';
  priorityCustomerId: string | null;
  priorityContactId: string | null;
  contactName: string | null;
  matches: number | null;
  syncedAt: string;
};

export type PrioritySyncSettings = {
  enabled: boolean;
  screen: 'customers' | 'phonebook';
  phoneField: 'main' | 'mobile' | 'work';
  createMissing: boolean;
  hasPriorityConnection?: boolean;
};

/** סנכרון ידני של איש קשר בודד (כפתור במסך אנשי קשר / תפריט שיחה). */
export async function fetchSyncContactWithPriority(contactId: number) {
  try {
    const raw = await fetcher(
      'post',
      ['/api/services/syncContactWithPriority', { data: { contactId } }],
      'servernode',
    );
    const data = (raw as { data?: PrioritySyncResult })?.data;
    return data ?? null;
  } catch (error) {
    console.warn('fetchSyncContactWithPriority error: ', error);
    return null;
  }
}

export async function fetchGetPrioritySyncSettings() {
  try {
    const raw = await fetcher('post', ['/api/services/getPrioritySyncSettings', { data: {} }], 'servernode');
    return ((raw as { data?: PrioritySyncSettings })?.data as PrioritySyncSettings) ?? null;
  } catch (error) {
    console.warn('fetchGetPrioritySyncSettings error: ', error);
    return null;
  }
}

export async function fetchSavePrioritySyncSettings(settings: Omit<PrioritySyncSettings, 'hasPriorityConnection'>) {
  try {
    const raw = await fetcher('post', ['/api/services/savePrioritySyncSettings', { data: settings }], 'servernode');
    return Boolean((raw as { success?: boolean })?.success);
  } catch (error) {
    console.warn('fetchSavePrioritySyncSettings error: ', error);
    return false;
  }
}

// ─── Document types (PrDocsPrefixes) ─────────────────────────────────────────
//* שורה = קידומת; לאותו doc_type יכולות להיות כמה שורות (סדרות מספור שונות בפריוריטי).
//* יצירה ומחיקה זורקות את השגיאה כדי שהמסך יציג את הודעת השרת (קידומת כפולה, קידומת אחרונה).
export interface PriorityDocumentType {
  id: string;
  doc_prefix: string;
  doc_type: string;
  doc_title: string | null;
  doc_search_index: string | null;
  date_field: string | null;
  is_active: boolean | null;
  created_on?: string;
  updated_on?: string;
}

export const fetchGetDocumentTypes = async (): Promise<PriorityDocumentType[]> => {
  const data = await fetcher('get', '/api/priority/document-types', 'servernode');
  return data?.result && Array.isArray(data.data) ? (data.data as PriorityDocumentType[]) : [];
};

export const fetchInitDocumentTypes = async (): Promise<PriorityDocumentType[]> => {
  const data = await fetcher('post', '/api/priority/document-types/init', 'servernode');
  return data?.result && Array.isArray(data.data) ? (data.data as PriorityDocumentType[]) : [];
};

export const fetchCreateDocumentType = async (
  docType: Pick<PriorityDocumentType, 'doc_prefix' | 'doc_type' | 'doc_title' | 'doc_search_index' | 'date_field'>,
): Promise<PriorityDocumentType | null> => {
  const data = await fetcher('post', ['/api/priority/document-types', { data: docType }], 'servernode');
  return data?.result && data.data ? (data.data as PriorityDocumentType) : null;
};

export const fetchDeleteDocumentType = async (id: string): Promise<boolean> => {
  const data = await fetcher('delete', `/api/priority/document-types/${id}`, 'servernode');
  return data?.result === true;
};
